import { Link } from 'react-router-dom';
import { Lockup } from '@/components/Logo';
import { contentBySection, latestNotes } from '@/lib/content';
import { EMAIL, PHONE, PHONE_E164, SITE_NAME, SITE_TAGLINE } from '@/lib/site';
import { tools } from '@/tools/registry';
import { ENGAGEMENTS } from '@/lib/engagements';

const buildYear = __BUILD_YEAR__;

interface Item {
  label: string;
  to: string;
}

const Column = ({ heading, items }: { heading: string; items: Item[] }) => (
  <div>
    <h2 className="label mb-4 text-brass-light">{heading}</h2>
    <ul className="space-y-1">
      {items.map((i) => (
        <li key={i.to}>
          <Link to={i.to} className="inline-block py-1 text-small font-bold underline-offset-[0.3em] hover:underline">
            {i.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const sectionItems = (section: 'upgrades' | 'migrations' | 'support', hubLabel: string): Item[] => [
  { label: hubLabel, to: `/${section}` },
  ...contentBySection(section)
    .slice(0, 4)
    .map((c) => ({ label: c.title, to: c.path })),
];

const Footer = () => {
  const toolsAndNotes: Item[] = [
    { label: 'All resources', to: '/resources' },
    { label: 'Free tools', to: '/tools' },
    ...tools.slice(0, 2).map((t) => ({ label: t.title, to: `/tools/${t.slug}` })),
    { label: 'Field notes', to: '/notes' },
    { label: 'Support guides', to: '/support#guides-heading' },
    ...latestNotes(3).map((n) => ({ label: n.title, to: n.path })),
  ];

  return (
    <footer className="on-forest border-t border-rule-dark bg-forest text-cream-light">
      <div className="container py-14">
        <div className="flex flex-col gap-4 border-b border-rule-dark pb-10 md:flex-row md:items-end md:justify-between">
          <div>
            <Lockup ground="dark" height={40} />
            <p className="label mt-4 text-brass-light">{SITE_TAGLINE}</p>
          </div>
          <p className="max-w-md text-small font-bold">
            Nearly 25 years of Oracle WebCenter delivery: documents, invoice workflows and the connections to
            E-Business Suite, PeopleSoft, JD Edwards and Fusion Cloud.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-10 py-12 sm:grid-cols-2 lg:grid-cols-5">
          <Column heading="Services" items={ENGAGEMENTS.map(({ to, label }) => ({ to, label }))} />
          <Column heading="Upgrade guides" items={sectionItems('upgrades', 'All upgrade guides')} />
          <Column heading="Migration guides" items={sectionItems('migrations', 'All migration guides')} />
          <Column heading="Tools & Notes" items={toolsAndNotes} />
          <Column
            heading="Firm"
            items={[
              { label: 'About', to: '/about' },
              { label: 'Talk to us', to: '/contact' },
              { label: 'Sectors', to: '/sectors' },
              { label: 'Privacy', to: '/privacy' },
              { label: 'Accessibility', to: '/accessibility' },
            ]}
          />
        </div>

        <div className="flex flex-col gap-3 border-t border-rule-dark pt-8 text-small font-bold md:flex-row md:items-center md:justify-between">
          <p>
            <a href={`mailto:${EMAIL}`} className="underline underline-offset-4">{EMAIL}</a>
            {PHONE_E164 && (
              <>
                <span aria-hidden className="mx-3">·</span>
                <a href={`tel:${PHONE_E164}`} className="underline underline-offset-4">{PHONE}</a>
              </>
            )}
          </p>
          <p>
            © {buildYear} {SITE_NAME}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
